import { useEffect, useRef, useState } from 'react';
import {
  init,
  loadRemote,
  registerRemotes,
} from '@module-federation/enhanced/runtime';
import { uuidv4 } from '../functions';

type RemoteState<Props extends object> =
  | { status: 'loading' }
  | { status: 'error'; details: unknown }
  | { status: 'success'; Component: React.FC<Props> };

const useDynamicRemote = <Props extends object>(
  remoteName: string,
  remoteUrl: string,
  moduleName: string
) => {
  const [state, setState] = useState<RemoteState<Props>>({ status: 'loading' });
  const [initChecked, setInitChecked] = useState(false);
  const registered = useRef<Record<string, boolean>>({});

  useEffect(() => {
    if (!initChecked) {
      if (!(globalThis as any).singleton_federation_host) {
        (globalThis as any).singleton_federation_host = init({
          name: 'host_app',
          remotes: [],
        });
      }
      setInitChecked(true);
    }
  }, [initChecked]);

  useEffect(() => {
    (async () => {
      try {
        if (!registered.current[remoteName]) {
          registerRemotes([
            { name: remoteName, entry: remoteUrl + '?query=' + uuidv4() }
          ], { force: true });
          registered.current[remoteName] = true;
        }
        const loadedModule = (await loadRemote(
          moduleName ? `${remoteName}/${moduleName}` : remoteName,
          { from: 'runtime' }
        )) as { default: React.FC<Props> };
        setState({ status: 'success', Component: loadedModule.default });
      } catch (e) {
        setState({ status: 'error', details: e });
      }
    })();
  }, [remoteName, remoteUrl, moduleName]);

  return state;
};

export default useDynamicRemote;
